import type { Ref, RefCallback } from "react"

/**
 * Small utility to merge multiple refs into one. This is useful when you want
 * to pass a forwarded ref to a component, but also want to have a ref on the
 * component itself.
 *
 * **Note:** This function _should_ always be called via `useMemo` to avoid
 * re-creating the function on every render, especially when forwarding refs
 * through animation wrappers.
 *
 * @param refs - An array of refs to merge.
 *
 * @example
 * const Example = ({ ref }) => {
 *   const fooRef = useRef(null);
 *   const mergedRefs = useMemo(() => mergeRefs(ref, fooRef), [ref, fooRef]);
 *   return <div ref={mergedRefs} />;
 * });
 */
export const mergeRefs = <TElement>(
  ...refs: Array<Ref<TElement> | undefined>
): RefCallback<TElement> => {
  return (element) => {
    // Iterate over all refs and assign the element to each of them
    const cleanups = refs.map((ref) => {
      if (typeof ref === "function") return ref(element)
      if (ref && "current" in ref) ref.current = element
    })

    // If any of the refs return a cleanup function call it on unmount
    return () => {
      for (const cleanup of cleanups) {
        if (typeof cleanup === "function") cleanup()
      }
    }
  }
}
